import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { hasUnit } from "@/models/nodes/utils";

import type { DocumentModel } from "@/models/DocumentModel";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { NodeModel } from "@/models/nodes/types";

function tryMergeInlineEditables(
  doc: DocumentModel,
  leftNode?: NodeModel,
  rightNode?: NodeModel,
  pos?: number,
  updateCursor?: boolean,
) {
  if (
    leftNode === rightNode ||
    !leftNode?.isInlineEditable() ||
    !leftNode.isMergeable ||
    !rightNode?.isInlineEditable() ||
    !rightNode.isMergeable ||
    leftNode.type !== rightNode.type
  )
    return;

  const valueLeft = leftNode.value;
  const valueRight = rightNode.value;

  if (!leftNode.validateValue(valueLeft + valueRight)) {
    return;
  }

  const leftHasPostfix = hasUnit(leftNode, "postfix");
  const leftHasSubSup =
    ("power" in leftNode && !!leftNode.power) || ("subscript" in leftNode && !!leftNode.subscript);
  const rightHasPrefix = hasUnit(rightNode, "prefix");

  const leftHasNodes = leftNode.nodes.length > 0;
  const rightHasNodes = rightNode.nodes.length > 0;

  // 1kg*2 or 1*$2
  if (leftHasPostfix || leftHasSubSup || rightHasPrefix) {
    return;
  }

  // $1k*2kg
  if (leftHasNodes && rightHasNodes) {
    return;
  }

  doc.transact(() => {
    const ns = leftHasNodes ? leftNode.nodes : rightNode.nodes;
    const nodes = ns.map((node) => createNodeFrom(node, doc, { resetIds: true }));

    leftNode.value = valueLeft + valueRight;
    leftNode.setChildren(nodes);
    doc.removeNode(rightNode);

    if (updateCursor) {
      doc.cursor.moveBeforeNode(leftNode);
      doc.input.editNode(leftNode, pos ?? valueLeft.length);
    }
  }, "tryMergeInlineEditables");
}

export function mergeInlineEditables(
  doc: DocumentModel,
  leftNode?: NodeModel,
  rightNode?: NodeModel,
  options?: { skipCursorUpdate?: boolean },
) {
  let nodeBefore = leftNode;
  let nodeAfter = rightNode;

  const { editingNode } = doc.input;

  if (!nodeBefore && !nodeAfter) {
    if (editingNode) {
      nodeBefore = editingNode.prevSiblings[0];
      nodeAfter = editingNode.nextSiblings[0];
    } else {
      nodeBefore = doc.cursor.nodeBeforeCursor;
      nodeAfter = doc.cursor.nodeAfterCursor;
    }
  }

  if (!editingNode) {
    let updateCursor = !options?.skipCursorUpdate;

    if (updateCursor) {
      updateCursor =
        nodeBefore === doc.cursor.nodeBeforeCursor && nodeAfter === doc.cursor.nodeAfterCursor;
    }

    tryMergeInlineEditables(doc, nodeBefore, nodeAfter, undefined, updateCursor);
  } else {
    let pos = doc.input.inputSelection?.from ?? 0;

    if (nodeBefore?.isInlineEditable()) {
      pos += nodeBefore.value.length;
      tryMergeInlineEditables(doc, nodeBefore, editingNode, pos, true);
    } else if (nodeAfter?.isInlineEditable()) {
      tryMergeInlineEditables(doc, editingNode, nodeAfter, pos, true);
    }
  }
}

export function mergeInlineEditablesInNode(doc: DocumentModel, node?: InlineNodeModel) {
  if (!node) return;

  for (let i = 0; i < node.nodes.length - 1; i++) {
    mergeInlineEditables(doc, node.nodes[i], node.nodes[i + 1]);
  }
}
